// frontend/src/components/VibeNames.js
//
// 'AI names from your vibe' — the second tab of the name generator on the
// handle pages (Growth round, T22; mockup .jarvis/design/AIVibe.dc.html).
//
// The visitor describes their vibe; POST /api/public/ai/vibe-names returns up
// to 8 handles, each with a one-line reason and its link-in-bio claim status.
// The platform's name rules are enforced on the server, so nothing here trims
// or re-checks a name.
//
// Logged out, that is one free try per day: after it (or when the server says
// the try is used, or the day's free AI is used up) the indigo card offers
// 'Get more AI ideas free' → sign-up. Logged in, the server spends AI credits.
// Indigo #4f46e5 marks the AI parts, as in AI Studio.
import React, { useState } from 'react'
import { Button, Row, Col } from 'react-bootstrap'
import { Clipboard, CheckLg, Heart, HeartFill } from 'react-bootstrap-icons'

import { signupHref } from '../utils/nameClaim'
import { fetchVibeNames, vibePlatformFor, VIBE_MAX } from '../utils/publicAi'
import { trackFunnel } from '../utils/funnel'

export const AI = '#4f46e5'

export function Sparkle ({ size = 16, twin = false, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox='0 0 24 24' fill={color} aria-hidden='true' focusable='false'>
      <path d={twin
        ? 'M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8zM19 14l.9 2.6 2.6.9-2.6.9L19 21l-.9-2.6-2.6-.9 2.6-.9z'
        : 'M12 2l1.8 5.2L19 9l-5.2 1.8L12 16l-1.8-5.2L5 9l5.2-1.8z'} />
    </svg>
  )
}

const PILL = { fontSize: 13, borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap', textDecoration: 'none' }

function ClaimPill ({ claim, from }) {
  if (!claim) return null
  if (claim.available) {
    return (
      <a href={signupHref({ claim: claim.username, from })} data-claim='free'
         onClick={() => trackFunnel('claim_click', from)}
         title={`greatlysocial.com/u/${claim.username} is free as your link in bio`}
         style={{ ...PILL, color: '#146c43', background: '#e8f5ee' }}>
        Link in bio free · Claim
      </a>
    )
  }
  if (claim.reason === 'Taken as a link in bio') {
    return (
      <span data-claim='taken' title='Taken as a link in bio'
            style={{ ...PILL, color: '#495057', background: '#f1f3f5' }}>
        Taken
      </span>
    )
  }
  return null
}

const CARDS = {
  free_try_used: {
    title: 'That was your free AI try.',
    body: 'Get more AI name ideas, bios and captions with a free Greatly Social account.',
  },
  daily_cap: {
    title: 'Our free AI is busy for today.',
    body: 'Try again tomorrow, or get more AI name ideas, bios and captions with a free Greatly Social account.',
  },
}

/** The indigo 'free try used' card (also used by the AI profile kit). */
export function FreeTryCard ({ kind, href }) {
  const card = CARDS[kind] || CARDS.free_try_used
  return (
    <div data-testid='ai-free-try-card' className='d-flex align-items-center flex-wrap'
         style={{ gap: 14, border: '1px solid #c7d2fe', background: '#eef2ff', borderRadius: 10, padding: '14px 16px' }}>
      <Sparkle size={22} twin color={AI} />
      <div style={{ flex: 1, minWidth: 200 }}>
        <div style={{ fontWeight: 700, color: '#3730a3' }}>{card.title}</div>
        <div style={{ fontSize: 14, color: '#4338ca' }}>{card.body}</div>
      </div>
      <a href={href}
         style={{
           height: 40, padding: '0 16px', borderRadius: 8, background: AI, color: '#fff', fontWeight: 700,
           fontSize: 14, textDecoration: 'none', display: 'flex', alignItems: 'center',
         }}>
        Get more AI ideas free
      </a>
    </div>
  )
}

const errorText = r => {
  if (r.status === 402) return 'You’ve used all your AI credits for this cycle. Upgrade your plan for more.'
  if (r.error === 'missing_vibe') return 'Describe your vibe in a few words.'
  if (r.message && r.status !== 0) return r.message
  return 'Could not find names right now. Try again in a moment.'
}

export default function VibeNames ({ cfg, copied, onCopy, isSaved, onToggleSaved, onClaims }) {
  const [vibe, setVibe] = useState('')
  const [names, setNames] = useState([])
  const [usedVibe, setUsedVibe] = useState('')  // the vibe `names` were found for
  const [loading, setLoading] = useState(false)
  const [card, setCard] = useState('')        // '' | 'free_try_used' | 'daily_cap'
  const [error, setError] = useState('')

  const find = async e => {
    if (e) e.preventDefault()
    const text = vibe.trim()
    if (!text) { setError('Describe your vibe in a few words.'); return }
    setError('')
    setLoading(true)
    const r = await fetchVibeNames({ vibe: text, platform: vibePlatformFor(cfg) })
    setLoading(false)
    if (r.ok) {
      setNames(r.data.names)
      setUsedVibe(text)
      trackFunnel('ai_vibe_used', cfg.slug)
      if (r.data.names.length) trackFunnel('names_generated', cfg.slug)
      setCard(r.data.freeTryUsed ? 'free_try_used' : '')
      // So the 'Nice pick' panel knows the claim status of a copied AI name.
      if (onClaims) {
        onClaims(Object.fromEntries(r.data.names.filter(n => n.claim && n.claim.username)
          .map(n => [n.claim.username, { available: !!n.claim.available, reason: n.claim.reason || '' }])))
      }
      return
    }
    // Used up: keep the names they already have and show the card instead.
    if (r.status === 429 && (r.error === 'free_try_used' || r.error === 'daily_cap')) { setCard(r.error); return }
    setError(errorText(r))
  }

  return (
    <div className='d-flex flex-column' style={{ gap: 16 }}>
      <form onSubmit={find} className='d-flex flex-column' style={{ gap: 6 }}>
        <label htmlFor={`vibe-${cfg.slug}`} style={{ fontWeight: 600, fontSize: 15 }}>
          Describe your vibe in a few words
        </label>
        <div className='d-flex flex-column flex-sm-row' style={{ gap: 10 }}>
          <input id={`vibe-${cfg.slug}`} className='form-control' value={vibe} maxLength={VIBE_MAX}
                 onChange={e => setVibe(e.target.value)}
                 placeholder='e.g. cozy autumn bookworm who loves tea'
                 aria-describedby={`vibe-hint-${cfg.slug}`}
                 style={{ flex: 1, height: 46, borderRadius: 8, fontSize: 16 }} />
          <button type='submit' className='btn' disabled={loading} aria-busy={loading}
                  style={{
                    height: 46, padding: '0 18px', border: 0, borderRadius: 8, background: AI, color: '#fff',
                    fontWeight: 700, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                  }}>
            <Sparkle size={16} />
            {loading ? 'Finding names…' : 'Find my names'}
          </button>
        </div>
        <div id={`vibe-hint-${cfg.slug}`} style={{ fontSize: 14, color: '#6c757d' }}>
          Not just pretty words. Names that match your vibe, with what they mean.
        </div>
      </form>

      {error && <p role='alert' className='text-danger small mb-0'>{error}</p>}

      {names.length > 0 && (
        <Row className='g-2' aria-busy={loading}>
          {names.map(n => {
            const saved = isSaved(n.name)
            return (
              <Col md={6} key={n.name}>
                <div data-testid='vibe-result' className='h-100 d-flex flex-column'
                     style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', gap: 6 }}>
                  <div className='d-flex align-items-center justify-content-between' style={{ gap: 8 }}>
                    <strong className='text-truncate' style={{ fontSize: 17, minWidth: 0 }}>@{n.name}</strong>
                    <div className='d-flex align-items-center flex-shrink-0' style={{ gap: 6 }}>
                      <ClaimPill claim={n.claim} from={cfg.slug} />
                      <Button size='sm' variant='light' onClick={() => onToggleSaved(n.name)}
                              aria-pressed={saved}
                              title={saved ? 'Remove from shortlist' : 'Save to shortlist'}
                              aria-label={saved ? `Remove ${n.name} from shortlist` : `Save ${n.name}`}
                              style={saved
                                ? { border: '1px solid #f5c2c7', background: '#fff0f1', color: '#b02a37' }
                                : { border: '1px solid #ced4da', background: '#fff', color: '#6c757d' }}>
                        {saved ? <HeartFill /> : <Heart />}
                      </Button>
                      <Button size='sm' variant='outline-secondary' onClick={() => onCopy(n.name, usedVibe)}
                              title='Copy' aria-label={`Copy ${n.name}`}>
                        {copied === n.name ? <CheckLg /> : <Clipboard />}
                      </Button>
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: '#495057' }}>{n.reason}</div>
                </div>
              </Col>
            )
          })}
        </Row>
      )}

      <div role='status' aria-live='polite'>
        {card && <FreeTryCard kind={card} href={signupHref({ from: cfg.slug })} />}
      </div>
    </div>
  )
}
